import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BrandingEditor from "@/components/BrandingEditor";
import AdminRepartidoresTab from "@/components/AdminRepartidoresTab";
import { AdminTransferenciasTab } from "@/components/AdminTransferenciasTab";
import { AdminSuscripcionesTab } from "@/components/AdminSuscripcionesTab";
import { AdminCuraduriaTab } from "@/components/AdminCuraduriaTab";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Store, Package, ShoppingCart, Truck, Palette, ShieldCheck, Landmark, Gem, Sparkles } from "lucide-react";

export default function PlazaAdminDashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [tiendas, setTiendas] = useState([]);
  const [repartidores, setRepartidores] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, t, r] = await Promise.all([
          api.get("/admin/overview"),
          api.get("/tiendas"),
          api.get("/repartidores"),
        ]);
        setOverview(o.data);
        setTiendas(t.data.tiendas);
        setRepartidores(r.data.repartidores);
      } catch (e) { toast.error(formatApiError(e)); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const asignarRepartidor = async (ordenId, repartidorId) => {
    try {
      await api.post(`/ordenes/${ordenId}/asignar-repartidor`, { repartidor_id: repartidorId });
      toast.success("Repartidor asignado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!overview) return <div className="min-h-screen bg-[#F9F9F8]"><Navbar /><div className="p-16 text-center text-zinc-500">Cargando…</div></div>;

  const stats = [
    { label: "Tiendas", value: overview.total_tiendas, Icon: Store, color: "text-[#FF3366]" },
    { label: "Productos", value: overview.total_productos, Icon: Package, color: "text-blue-600" },
    { label: "Órdenes activas", value: overview.ordenes_activas, Icon: ShoppingCart, color: "text-amber-600" },
    { label: "Repartidores", value: overview.total_repartidores, Icon: Truck, color: "text-emerald-600" },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline text-zinc-500">Administración de plaza</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">Panel del Mall</h1>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="rounded-sm bg-white border border-zinc-200 p-1 h-11">
            <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4">Overview</TabsTrigger>
            <TabsTrigger value="repartidores" data-testid="admin-tab-repartidores" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Repartidores</TabsTrigger>
            <TabsTrigger value="branding" data-testid="admin-tab-branding" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4"><Palette className="w-3.5 h-3.5 mr-1" /> Branding</TabsTrigger>
            <TabsTrigger value="transferencias" data-testid="admin-tab-transferencias" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4"><Landmark className="w-3.5 h-3.5 mr-1" /> Transferencias</TabsTrigger>
            <TabsTrigger value="suscripciones" data-testid="admin-tab-suscripciones" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4"><Gem className="w-3.5 h-3.5 mr-1" /> Suscripciones</TabsTrigger>
            <TabsTrigger value="curaduria" data-testid="admin-tab-curaduria" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white px-4"><Sparkles className="w-3.5 h-3.5 mr-1" /> Curaduría</TabsTrigger>
          </TabsList>

          <TabsContent value="curaduria" className="mt-6">
            <AdminCuraduriaTab />
          </TabsContent>

          <TabsContent value="suscripciones" className="mt-6">
            <AdminSuscripcionesTab />
          </TabsContent>

          <TabsContent value="transferencias" className="mt-6">
            <AdminTransferenciasTab />
          </TabsContent>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {stats.map(s => (
                <div key={s.label} data-testid={`stat-${s.label}`} className="bg-white border border-zinc-200 rounded-sm p-5">
                  <s.Icon className={`w-5 h-5 ${s.color}`} />
                  <div className="mt-3 font-display font-black text-3xl tracking-tighter">{s.value}</div>
                  <div className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6 mt-8">
              <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm">
                <div className="p-5 border-b border-zinc-100">
                  <div className="font-display font-bold tracking-tight text-lg">Órdenes recientes</div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {overview.ordenes_recientes.length === 0 && (
                    <div className="p-8 text-center text-zinc-400 text-sm">Aún no hay órdenes</div>
                  )}
                  {overview.ordenes_recientes.map(o => (
                    <div key={o.id} data-testid={`admin-orden-${o.id}`} className="p-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-mono font-bold text-sm">{o.token_seguridad}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{o.cliente_nombre} · {new Date(o.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-zinc-100 text-zinc-700 border-0">{o.estado}</Badge>
                        <span className="font-display font-bold text-sm">${o.total.toFixed(2)}</span>
                        {o.estado === "Listo_Para_PickUp" && !o.repartidor_id && (
                          <select
                            data-testid={`assign-${o.id}`}
                            onChange={e => e.target.value && asignarRepartidor(o.id, e.target.value)}
                            className="text-xs border border-zinc-300 rounded-sm h-8 px-2"
                            defaultValue=""
                          >
                            <option value="" disabled>Asignar repartidor…</option>
                            {repartidores.filter(r => r.estado_disponibilidad === "Libre").map(r => (
                              <option key={r.id} value={r.id}>{r.nombre}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-zinc-200 rounded-sm">
                  <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
                    <div className="font-display font-bold tracking-tight text-lg">Repartidores</div>
                    <span className="text-xs text-zinc-500">{repartidores.filter(r => r.estado_disponibilidad === "Libre").length} libres</span>
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {repartidores.map(r => (
                      <div key={r.id} className="p-4 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-sm">{r.nombre}</div>
                          <div className="text-xs text-zinc-500">{r.vehiculo}</div>
                        </div>
                        <Badge className={
                          r.estado_disponibilidad === "Libre" ? "bg-emerald-100 text-emerald-700 border-0" :
                          r.estado_disponibilidad === "Ocupado" ? "bg-amber-100 text-amber-700 border-0" :
                          "bg-zinc-200 text-zinc-600 border-0"
                        }>{r.estado_disponibilidad}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border border-zinc-200 rounded-sm">
                  <div className="p-5 border-b border-zinc-100">
                    <div className="font-display font-bold tracking-tight text-lg">Tiendas</div>
                  </div>
                  <div className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
                    {tiendas.map(t => (
                      <div key={t.id} className="p-4">
                        <div className="font-medium text-sm">{t.nombre_local}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Local {t.numero_local} · {t.categoria}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="repartidores" className="mt-6">
            <AdminRepartidoresTab />
          </TabsContent>

          <TabsContent value="branding" className="mt-6">
            <BrandingEditor plazaId={user.plaza_id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
